import {
  Component,
  input,
  output,
  ElementRef,
  AfterViewInit,
  signal,
  computed,
  inject,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from "@angular/core";
import { cn } from "../utils/utils";
import { injectChatLabels } from "../core/chat-labels";
import { injectChatState } from "../core/chat-state";

@Component({
  selector: "textarea[agentChatTextarea]",
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    "[value]": "computedValue()",
    "[placeholder]": "placeholder()",
    "[disabled]": "disabled()",
    "[class]": "computedClass()",
    "[style.min-height.px]": "48", // Ensure minimum height
    "[style.overflow]": "'auto'",
    "[style.resize]": "'none'",
    "(input)": "onInput($event)",
    "(keydown)": "onKeyDown($event)",
    "[attr.rows]": "1",
  },
  template: "",
})
export class AgentChatTextarea implements AfterViewInit {
  private elementRef = inject(ElementRef<HTMLTextAreaElement>);

  get textareaRef() {
    return this.elementRef;
  }

  inputValue = input<string | undefined>();

  inputPlaceholder = input<string | undefined>();

  inputMaxRows = input<number | undefined>();

  inputAutoFocus = input<boolean | undefined>();

  inputDisabled = input<boolean | undefined>();

  inputClass = input<string | undefined>();

  valueChange = output<string>();

  keyDown = output<KeyboardEvent>();

  readonly chatLabels = injectChatLabels();

  readonly chatState = injectChatState();

  maxHeight = signal<number>(200); // Default reasonable max height

  computedValue = computed(() => this.inputValue() ?? this.chatState.inputValue() ?? "");

  placeholder = computed(() => this.inputPlaceholder() || this.chatLabels.chatInputPlaceholder);

  disabled = computed(() => this.inputDisabled() ?? false);

  computedClass = computed(() => cn("achat-textarea", this.inputClass()));

  constructor() {}

  ngAfterViewInit(): void {
    this.calculateMaxHeight();
    this.adjustHeight();

    if (this.inputAutoFocus() ?? true) {
      setTimeout(() => {
        this.elementRef.nativeElement.focus();
      });
    }

    // Set max-height via direct style manipulation to avoid binding issues
    setTimeout(() => {
      const textarea = this.elementRef.nativeElement;
      const maxHeightValue = this.maxHeight();
      if (maxHeightValue > 0) {
        textarea.style.maxHeight = `${maxHeightValue}px`;
      }
    });
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    const newValue = textarea.value;
    this.valueChange.emit(newValue);
    this.chatState.changeInput(newValue);
    this.adjustHeight();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.keyDown.emit(event);
    } else {
      this.keyDown.emit(event);
    }
  }

  private calculateMaxHeight(): void {
    const textarea = this.elementRef.nativeElement;
    const maxRowsValue = this.inputMaxRows() ?? 5;

    // Set a temporary single line to measure line height
    const currentValue = textarea.value;
    textarea.value = "M"; // Single character for measurement
    textarea.style.height = "auto";
    textarea.style.overflow = "hidden";

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;

    // Calculate max height based on line height and max rows
    const calculatedMaxHeight = lineHeight * maxRowsValue + paddingTop + paddingBottom + borderTop + borderBottom;

    // Ensure minimum height (at least 48px for single line)
    const minHeight = Math.max(48, lineHeight + paddingTop + paddingBottom + borderTop + borderBottom);

    this.maxHeight.set(Math.max(calculatedMaxHeight, minHeight));

    // Restore original value and adjust height
    textarea.value = currentValue;
    textarea.style.overflow = "auto";

    if (currentValue) {
      this.adjustHeight();
    }
  }

  private adjustHeight(): void {
    const textarea = this.elementRef.nativeElement;
    const maxHeightValue = this.maxHeight();

    if (maxHeightValue > 0) {
      // Reset height to auto to get accurate scrollHeight
      textarea.style.height = "auto";

      // Calculate the new height based on content
      const newHeight = Math.min(textarea.scrollHeight, maxHeightValue);

      // Ensure minimum height of 48px
      const finalHeight = Math.max(48, newHeight);

      textarea.style.height = `${finalHeight}px`;
      textarea.style.maxHeight = `${maxHeightValue}px`;
    }
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  getValue(): string {
    return this.elementRef.nativeElement.value;
  }

  setValue(value: string): void {
    this.elementRef.nativeElement.value = value;
    this.valueChange.emit(value);
    this.chatState.changeInput(value);
    setTimeout(() => this.adjustHeight());
  }
}
